<template>
    <v-card-title>
        <span class="table-header table-header--black">{{ reportTitle }}</span>
        <v-divider vertical class="mx-3" />
        <span class="table-header table-header--gray">{{ academicPeriod }}</span>
        <v-spacer></v-spacer>

        <a
            :href="downloadRoute"
            :download="reportFileName"
            class="table-download-file"
            v-if="isDownloadable"
        >
            <v-btn
                v-if="checkPermission('REPORTS_TYPE_REPORT_DOWNLOAD')"
                class="v-btn--primary">{{ $t('reports.download') }}</v-btn>
        </a>

        <v-menu v-if="showMoreOptions" left offset-y rounded="0">
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" class="ml-2">
                    <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
            </template>

            <v-list class="list">
                <v-list-item
                    v-for="(item, index) in menuItems"
                    v-if="showItem(item.title)"
                    :key="index"
                    link
                    dense
                    :disabled="item.disabled(report)"
                    @click="item.action(report)"
                    :class="item.class ? 'list--item ' + item.class : 'list--item'"
                >
                    <v-list-item-icon>
                        <v-icon>{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-menu>

        <edit-dialog
            :show-dialog="flags.showEditDialog"
            :report="report"
            :reportType="type"
            @cancelReportUpdate="hideEditReportDialog"
        />

        <delete-report-dialog
            :showDialog="flags.showDeleteDialog"
            :report="report"
            v-on:confirmReportDeletion="removeReport"
            v-on:cancelReportDeletion="hideReportDeletionDialog"
        />

    </v-card-title>
</template>

<script>
import deleteReportDialog from "../Index/DeleteReportDialog";
import editDialog from "./EditDialog";
import {mapActions} from 'pinia';
import {ReportsStore} from "@/Store/Reports/ReportsStore";
import {EventBus} from "@/event-bus";
import {i18n} from "@/i18n";
import toastMixin from "@/Shared/toastMixin";
import _ from "lodash";
import {mapState} from "pinia/dist/pinia";
import {PermissionsStore} from "@/Store/PermissionsStore";

export default {
    name: "ReportCardTitle",

    props: {
        report: {
            type: Object,
            required: true,
        },
        type: {
            type: Object,
            required: true,
        },
    },

    components: {deleteReportDialog, editDialog},

    mixins: [toastMixin],

    data() {
        return {

            flags: {
                showDeleteDialog: false,
                showEditDialog: false,
            },

            menuItems: [
                {
                    title: i18n.t('app.edit'),
                    icon: "mdi-account-edit",
                    action: report => this.showEditReportDialog(),
                    disabled: report => false,
                },
                {
                    title: i18n.t('reports.add_note'),
                    icon: "mdi-note-plus",
                    disabled: report => true,
                    // action: 'showAddNote',
                },
                {
                    title: i18n.t('app.delete'),
                    icon: "mdi-delete",
                    class: "list--item__remove",
                    disabled: report => !this.canBeDeleted(report),
                    action: () => this.showDeleteConfirmationDialog(),
                },
            ],
        }
    },

    methods: {

        /**
         * Shows dialog to delete given report
         */
        showDeleteConfirmationDialog() {
            this.flags.showDeleteDialog = true;
        },

        /**
         * Hides dialog
         */
        hideReportDeletionDialog() {
            this.flags.showDeleteDialog = false;
        },

        /**
         * Shows edit dialog
         */
        showEditReportDialog() {
            this.flags.showEditDialog = true;
        },

        /**
         * Hides edition dialog
         */
        hideEditReportDialog() {
            this.flags.showEditDialog = false;
        },

        /**
         *
         */
        goToReportsListView() {
            this.$inertia.get(route('reports.index', {type: _.get(this.type, 'route_type_parameter')}));
        },

        /**
         * Deletes selected report
         *
         */
        async removeReport() {
            try {

                await this.deleteReport(_.cloneDeep(this.report));

                this.toast().success(
                    this.$t('reports.report_view.success_delete_title', {reportTitle: this.reportTitle}),
                );

                this.goToReportsListView();

            } catch (ex) {
                console.log(ex);

                this.toast().error(
                    this.$t('reports.report_view.error_delete_title', {reportTitle: this.reportTitle}),
                    this.$t('reports.report_view.error_delete_message'),
                );

            } finally {

                EventBus.$emit('reports.management.delete-report');

                this.hideReportDeletionDialog();
            }
        },

        /**
         *
         * @param Report
         * @returns {boolean}
         */
        canBeDeleted(Report) {
            return !['IN_PROCESS', 'FAILED'].includes(Report.status);
        },

        showItem (title) {
          switch (title) {
              case i18n.t('app.edit'):
                  return this.checkPermission('REPORTS_TYPE_REPORT_EDIT');
              case i18n.t('reports.add_note'):
                  return this.checkPermission('REPORTS_TYPE_REPORT_ADD_NOTE');
              case i18n.t('app.delete'):
                  return this.checkPermission('REPORTS_TYPE_REPORT_DELETE');
          }
        },

        /**
         *
         */
        ...mapActions(ReportsStore, [
            'deleteReport'
        ]),

        ...mapActions(PermissionsStore, ['checkPermission']),
    },

    computed: {
        /**
         * @returns {*}
         */
        academicPeriod() {
            return _.get(this.report, 'academic_period.name', '-')
        },

        /**
         *
         * @returns {*}
         */
        reportFileName() {
            return _.get(this.report, 'report_file.file_name')
        },

        /**
         *
         * @returns {*}
         */
        downloadRoute() {
            return route('reports.download', {report: this.report.id});
        },

        /**
         *
         * @returns {*}
         */
        reportTitle() {
            return _.get(this.report, 'title');
        },

        /**
         * Checks is the report can be downloaded
         *
         * @returns {boolean}
         */
        isDownloadable() {
            return !['IN_PROCESS', 'FAILED'].includes(this.report.status);
        },

        showMoreOptions() {
            return this.checkPermission('REPORTS_TYPE_REPORT_EDIT') || this.checkPermission('REPORTS_TYPE_REPORT_ADD_NOTE') || this.checkPermission('REPORTS_TYPE_REPORT_DELETE');
        },
    },

    created() {
        this.$root.$on('cancelReportUpdate', this.hideEditReportDialog)
    }
}
</script>

<style scoped>

    .table-download-file {
        text-decoration: none
    }

</style>
